import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
} from "@mui/material";
import { userActivityReportUsers } from "../../../../../services/api/types/reports/reports";

const ACTIVITY_TABLE_MIN_WIDTH = 1170;
const SCROLL_ARROW_W = 44;

interface UserActivityTableProps {
  data: userActivityReportUsers[];
}

export const UserActivityTable: React.FC<UserActivityTableProps> = ({
  data,
}) => {
  const borderColor = "#EAECF0";
  const headerBg = "#F9FAFB";

  const tableScrollRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    startX: number;
    startScrollLeft: number;
    trackW: number;
    thumbW: number;
  } | null>(null);
  const [scrollbar, setScrollbar] = useState({
    show: false,
    thumbWidth: 0,
    thumbLeft: 0,
  });

  const refreshScrollbar = useCallback(() => {
    const el = tableScrollRef.current;
    if (!el || dragRef.current) return;
    const scrollable = el.scrollWidth > el.clientWidth + 1;
    if (!scrollable) {
      setScrollbar((s) => ({ ...s, show: false }));
      return;
    }
    const trackW = el.clientWidth - SCROLL_ARROW_W * 2;
    const thumbW = Math.max((el.clientWidth / el.scrollWidth) * trackW, 44);
    const maxScroll = el.scrollWidth - el.clientWidth;
    const thumbLeft =
      maxScroll > 0 ? (el.scrollLeft / maxScroll) * (trackW - thumbW) : 0;
    setScrollbar({ show: true, thumbWidth: thumbW, thumbLeft });
  }, []);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", refreshScrollbar, { passive: true });
    const ro = new ResizeObserver(refreshScrollbar);
    ro.observe(el);
    refreshScrollbar();
    return () => {
      el.removeEventListener("scroll", refreshScrollbar);
      ro.disconnect();
    };
  }, [refreshScrollbar]);

  useEffect(() => {
    const move = (clientX: number) => {
      const d = dragRef.current;
      if (!d) return;
      const el = tableScrollRef.current;
      if (!el) return;
      const maxScroll = el.scrollWidth - el.clientWidth;
      const scale = maxScroll / Math.max(d.trackW - d.thumbW, 1);
      const newScrollLeft = Math.max(
        0,
        Math.min(maxScroll, d.startScrollLeft + (clientX - d.startX) * scale),
      );
      el.scrollLeft = newScrollLeft;
      if (thumbRef.current) {
        const newLeft =
          maxScroll > 0
            ? (newScrollLeft / maxScroll) * (d.trackW - d.thumbW)
            : 0;
        thumbRef.current.style.left = `${newLeft}px`;
      }
    };
    const end = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      refreshScrollbar();
    };
    const onMM = (e: MouseEvent) => move(e.clientX);
    const onTM = (e: TouchEvent) => {
      if (!dragRef.current) return;
      e.preventDefault();
      move(e.touches[0].clientX);
    };
    document.addEventListener("mousemove", onMM);
    document.addEventListener("mouseup", end);
    document.addEventListener("touchmove", onTM, { passive: false });
    document.addEventListener("touchend", end);
    return () => {
      document.removeEventListener("mousemove", onMM);
      document.removeEventListener("mouseup", end);
      document.removeEventListener("touchmove", onTM);
      document.removeEventListener("touchend", end);
    };
  }, [refreshScrollbar]);

  const onThumbDragStart = (clientX: number) => {
    const el = tableScrollRef.current;
    if (!el) return;
    dragRef.current = {
      startX: clientX,
      startScrollLeft: el.scrollLeft,
      trackW: el.clientWidth - SCROLL_ARROW_W * 2,
      thumbW: scrollbar.thumbWidth,
    };
  };

  const scrollByAmount = (amount: number) => {
    tableScrollRef.current?.scrollBy({ left: amount, behavior: "smooth" });
  };

  // Add safety check
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${borderColor}`,
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow: "0px 1px 3px 0px #1018281A",
        }}
      >
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography color="#475467" fontSize={14}>
            No activity data found.
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${borderColor}`,
        borderRadius: "8px",
        overflow: "hidden",
        boxShadow: "0px 1px 3px 0px #1018281A",
        p: 0,
      }}
    >
      <TableContainer
        ref={tableScrollRef}
        sx={{
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-x pan-y",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Table sx={{ minWidth: ACTIVITY_TABLE_MIN_WIDTH }}>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: headerBg,
                "& th": {
                  fontSize: 12,
                  color: "#475467",
                  fontWeight: 500,
                  borderBottom: `1px solid ${borderColor}`,
                  py: 1.5,
                  px: 2,
                },
              }}
            >
              <TableCell
                sx={{
                  width: 180,
                  position: { xs: "sticky", md: "static" },
                  left: { xs: 0, md: "auto" },
                  zIndex: { xs: 2, md: "auto" },
                  backgroundColor: headerBg,
                }}
              >
                Actioned by
              </TableCell>
              <TableCell sx={{ width: 180 }}>Target user email</TableCell>
              <TableCell sx={{ width: 300 }}>Activity</TableCell>
              <TableCell sx={{ width: 350 }}>Activity details</TableCell>
              <TableCell sx={{ width: 160 }}>Activity time stamp</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((activity, index) => {
              const rowBg = index % 2 === 0 ? "#fff" : "#F9FAFB";
              return (
                <TableRow
                  key={`${activity.userUid}-${index}`}
                  sx={{
                    backgroundColor: rowBg,
                    "& td": {
                      fontSize: 12,
                      color: "#475467",
                      borderBottom: `1px solid ${borderColor}`,
                      py: 1.5,
                      px: 2,
                      verticalAlign: "top",
                    },
                    "&:last-child td": {
                      borderBottom: "none",
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      width: 180,
                      position: { xs: "sticky", md: "static" },
                      left: { xs: 0, md: "auto" },
                      zIndex: { xs: 1, md: "auto" },
                      backgroundColor: { xs: rowBg, md: "inherit" },
                    }}
                  >
                    <Typography color="#475467" fontSize={12}>
                      {activity.email}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: 180 }}>
                    <Typography color="#475467" fontSize={12}>
                      {activity.actionOnUser || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: 300 }}>
                    <Typography color="#475467" fontSize={12}>
                      {activity.activity}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: 350 }}>
                    <Typography color="#475467" fontSize={12}>
                      {activity.activityDetails}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: 160 }}>
                    <Typography color="#475467" fontSize={12}>
                      {activity.activityTimestamp}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {scrollbar.show && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: SCROLL_ARROW_W,
            backgroundColor: "#fff",
            userSelect: "none",
          }}
        >
          <Box
            sx={{
              width: SCROLL_ARROW_W,
              height: SCROLL_ARROW_W,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
            }}
            onMouseDown={() => scrollByAmount(-100)}
            onTouchStart={(e) => {
              e.preventDefault();
              scrollByAmount(-100);
            }}
          >
            <svg width="10" height="16" viewBox="0 0 6 10">
              <polygon points="5,1 1,5 5,9" fill="#344054" />
            </svg>
          </Box>
          <Box sx={{ flex: 1, position: "relative", height: SCROLL_ARROW_W }}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                height: 16,
                width: scrollbar.thumbWidth,
                left: scrollbar.thumbLeft,
                backgroundColor: "#eaecf0",
                borderRadius: "10px",
                cursor: "grab",
                touchAction: "none",
                "&:active": { cursor: "grabbing" },
              }}
              ref={thumbRef}
              onMouseDown={(e) => {
                e.preventDefault();
                onThumbDragStart(e.clientX);
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                onThumbDragStart(e.touches[0].clientX);
              }}
            />
          </Box>
          <Box
            sx={{
              width: SCROLL_ARROW_W,
              height: SCROLL_ARROW_W,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
            }}
            onMouseDown={() => scrollByAmount(100)}
            onTouchStart={(e) => {
              e.preventDefault();
              scrollByAmount(100);
            }}
          >
            <svg width="10" height="16" viewBox="0 0 6 10">
              <polygon points="1,1 5,5 1,9" fill="#344054" />
            </svg>
          </Box>
        </Box>
      )}
    </Paper>
  );
};
